import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, Building2, Mail, Lock, User, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OwnerSignUp() {
    const navigate = useNavigate();
    const { ownerSignUp } = useAuth();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [moduleDefs, setModuleDefs] = useState([]);
    const [selectedModules, setSelectedModules] = useState({});
    const [formData, setFormData] = useState({
        company_name: '', company_slug: '', full_name: '', email: '', password: '', confirmPassword: ''
    });

    useEffect(() => {
        axios.get(`${API}/api/modules/definitions`)
            .then(r => {
                setModuleDefs(r.data);
                const defaults = {};
                r.data.forEach(m => { defaults[m.key] = m.default || m.core; });
                setSelectedModules(defaults);
            })
            .catch(() => {});
    }, []);

    const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    const handleCompanyNameChange = (e) => {
        const name = e.target.value;
        setFormData(prev => ({ ...prev, company_name: name, company_slug: generateSlug(name) }));
    };

    const handleNext = (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) { toast.error('Sifreler eslesmiyor'); return; }
        if (formData.password.length < 6) { toast.error('Sifre en az 6 karakter olmali'); return; }
        if (!formData.company_slug) { toast.error('Sirket slug gerekli'); return; }
        setStep(2);
    };

    const toggleModule = (key) => {
        const def = moduleDefs.find(m => m.key === key);
        if (def?.core) return;
        setSelectedModules(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await ownerSignUp({
                company_name: formData.company_name,
                company_slug: formData.company_slug,
                full_name: formData.full_name,
                email: formData.email,
                password: formData.password,
                modules: selectedModules
            });
            toast.success('Hesap olusturuldu');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Hesap olusturulamadi');
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors mb-8" data-testid="back-link">
                    <ArrowLeft size={20} /> <span>Ana sayfa</span>
                </Link>

                <div className="card p-8">
                    {/* Step indicator */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= 1 ? 'bg-[#111827] text-white' : 'bg-[#E5E7EB] text-[#9CA3AF]'}`}>1</div>
                        <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-[#111827]' : 'bg-[#E5E7EB]'}`} />
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= 2 ? 'bg-[#111827] text-white' : 'bg-[#E5E7EB] text-[#9CA3AF]'}`}>2</div>
                    </div>

                    {step === 1 && (
                        <>
                            <div className="text-center mb-6">
                                <h1 className="text-2xl font-semibold text-gray-900 mb-1">Hesap Olustur</h1>
                                <p className="text-gray-500 text-sm">Sirket bilgilerinizi girin</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                    const redirectUrl = window.location.origin + '/auth-callback';
                                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                                }}
                                className="w-full h-11 mb-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-800 flex items-center justify-center gap-2 transition"
                                data-testid="google-signup-btn"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.85 14.12c-.22-.66-.34-1.36-.34-2.12s.12-1.46.34-2.12V7.04H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.96l3.67-2.84z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                                </svg>
                                Google ile devam et
                            </button>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex-1 h-px bg-gray-200" />
                                <span className="text-xs text-gray-400">veya</span>
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>
                            <form onSubmit={handleNext} className="space-y-4">
                                <div>
                                    <label className="form-label">Sirket Adi</label>
                                    <div className="relative">
                                        <Building2 className="form-icon" size={18} />
                                        <input type="text" value={formData.company_name} onChange={handleCompanyNameChange}
                                            placeholder="Flowbil Fabrika" className="form-input form-input-icon" required data-testid="company-name-input" />
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Sirket Slug</label>
                                    <input type="text" value={formData.company_slug}
                                        onChange={(e) => setFormData(prev => ({ ...prev, company_slug: generateSlug(e.target.value) }))}
                                        placeholder="flowbil-fabrika" className="form-input" required data-testid="company-slug-input" />
                                    <p className="text-xs text-gray-400 mt-1">Calisan girisi icin URL'de kullanilir</p>
                                </div>
                                <div>
                                    <label className="form-label">Ad Soyad</label>
                                    <div className="relative">
                                        <User className="form-icon" size={18} />
                                        <input type="text" value={formData.full_name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                                            placeholder="Ahmet Yilmaz" className="form-input form-input-icon" required data-testid="full-name-input" />
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Email</label>
                                    <div className="relative">
                                        <Mail className="form-icon" size={18} />
                                        <input type="email" value={formData.email}
                                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="ahmet@flowbil.com" className="form-input form-input-icon" required data-testid="email-input" />
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Sifre</label>
                                    <div className="relative">
                                        <Lock className="form-icon" size={18} />
                                        <input type="password" value={formData.password}
                                            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                            placeholder="••••••••" className="form-input form-input-icon" required data-testid="password-input" />
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Sifre Tekrar</label>
                                    <div className="relative">
                                        <Lock className="form-icon" size={18} />
                                        <input type="password" value={formData.confirmPassword}
                                            onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                            placeholder="••••••••" className="form-input form-input-icon" required data-testid="confirm-password-input" />
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary w-full py-3 mt-6 flex items-center justify-center gap-2" data-testid="next-step-btn">
                                    Devam Et <ArrowRight size={16} />
                                </button>
                            </form>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="text-center mb-5">
                                <h1 className="text-2xl font-semibold text-gray-900 mb-1">Modulleri Secin</h1>
                                <p className="text-gray-500 text-sm">Ihtiyaciniz olan modulleri aktif edin</p>
                            </div>

                            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 mb-5" data-testid="module-selection">
                                {moduleDefs.map(m => {
                                    const enabled = selectedModules[m.key];
                                    return (
                                        <button key={m.key} type="button"
                                            onClick={() => toggleModule(m.key)}
                                            disabled={m.core}
                                            className={`w-full text-left flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-all ${enabled ? 'border-[#059669] bg-[#F0FDF4]' : 'border-[#E5E7EB] bg-white hover:border-[#9CA3AF]'} ${m.core ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                                            data-testid={`signup-module-${m.key}`}
                                        >
                                            <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${enabled ? 'bg-[#059669] text-white' : 'bg-[#E5E7EB]'}`}>
                                                {enabled && <Check size={12} strokeWidth={3} />}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-sm font-medium text-[#111]">{m.name}</span>
                                                    {m.core && <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0 rounded bg-[#E5E7EB] text-[#6B7280]">Temel</span>}
                                                </div>
                                                <p className="text-[11px] text-[#9CA3AF] leading-tight">{m.description}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex gap-2">
                                <button onClick={() => setStep(1)} className="flex-1 py-3 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] transition-colors" data-testid="back-step-btn">
                                    Geri
                                </button>
                                <button onClick={handleSubmit} disabled={loading}
                                    className="flex-1 btn btn-primary py-3 flex items-center justify-center gap-2" data-testid="signup-btn">
                                    {loading ? 'Olusturuluyor...' : 'Hesabi Olustur'}
                                </button>
                            </div>
                        </>
                    )}

                    <p className="text-center text-gray-500 mt-6">
                        Zaten hesabiniz var mi?{' '}
                        <Link to="/signin" className="text-gray-900 hover:underline font-medium" data-testid="signin-link">Giris yap</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
